import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FactCheckOutlined } from '@mui/icons-material';
import type { PatientV2RegulatoryDecisionPayload } from '../../../../services/patients/patientsV2Service';
import type { PatientV2Detail } from './types';
import { BrighterGreen, DarkGreen, PrimaryTitleGray } from '../../../../app/theme/tokens/colors';

const options: Array<{ value: PatientV2RegulatoryDecisionPayload['decision']; label: string; description: string }> = [
  { value: 'Deferido', label: 'Deferido', description: 'Aprova a solicitação e ativa o paciente.' },
  { value: 'Deferido provisório', label: 'Deferido provisório', description: 'Ativa o paciente com observação regulatória.' },
  { value: 'Indeferimento provisório', label: 'Indeferimento provisório', description: 'Não ativa e permite correção e reenvio.' },
  { value: 'Indeferido', label: 'Indeferido', description: 'Encerra a solicitação sem ativação.' },
];

const decisionStyle: Record<PatientV2RegulatoryDecisionPayload['decision'], { border: string; background: string }> = {
  Deferido: { border: '#4a9a5d', background: '#f1f8f2' },
  'Deferido provisório': { border: '#4c8fbd', background: '#f0f7fc' },
  'Indeferimento provisório': { border: '#c78524', background: '#fff7e9' },
  Indeferido: { border: '#c95a50', background: '#fdf1f0' },
};

const hasDecisionStyle = (value: string): value is PatientV2RegulatoryDecisionPayload['decision'] => Object.prototype.hasOwnProperty.call(decisionStyle, value);

const formatDateTime = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('pt-BR');
};

const statusStyle = (status: string) => {
  const label = String(status || '—');
  if (['Ativo', 'Aprovado', 'Deferido'].includes(label)) return { label, color: '#287a20', backgroundColor: '#eaf6e6' };
  if (['Deferido provisório'].includes(label)) return { label, color: '#287a20', backgroundColor: '#edf7f0' };
  if (['Pendente', 'Pendenciado', 'Reavaliação pendente', 'Indeferimento provisório'].includes(label)) return { label, color: '#a86200', backgroundColor: '#fff1dc' };
  if (['Indeferido', 'Não aprovado'].includes(label)) return { label, color: '#b42318', backgroundColor: '#fcebe8' };
  return { label, color: '#1769aa', backgroundColor: '#eaf4fb' };
};

const RegulatoryStatusTag = ({ status }: { status?: string }) => {
  const style = statusStyle(status || 'Em análise');
  return <Chip label={style.label} size="small" sx={{ alignSelf: 'flex-start', color: style.color, backgroundColor: style.backgroundColor, fontWeight: 700 }} />;
};

const RegulatoryDecisionTag = ({ decision }: { decision: string }) => {
  if (!hasDecisionStyle(decision)) {
    return <Chip label={decision} size="small" sx={{ color: '#1769aa', backgroundColor: '#eaf4fb', fontWeight: 700 }} />;
  }
  const style = decisionStyle[decision];
  return <Chip label={decision} size="small" sx={{ color: style.border, backgroundColor: style.background, border: `1px solid ${style.border}`, fontWeight: 700 }} />;
};

const RegulatoryMeta = ({ patient, analyst = '—', analyzedAt = '—' }: { patient: PatientV2Detail; analyst?: string; analyzedAt?: string }) => (
  <Stack spacing={2.1}>
    <Box>
      <Typography variant="caption" color="text.secondary">
        Status atual
      </Typography>
      <Box sx={{ mt: 0.35 }}>
        <RegulatoryStatusTag status={patient.regulatoryStatus} />
      </Box>
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary">
        Analista responsável
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600 }}>
        {analyst}
      </Typography>
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary">
        Data da análise
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600 }}>
        {analyzedAt}
      </Typography>
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary">
        Prazo de análise (SLA)
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600 }}>
        —
      </Typography>
    </Box>
  </Stack>
);

const RegulatoryReadOnly = ({
  patient,
  analyst,
  analyzedAt,
  decision,
  observation,
}: {
  patient: PatientV2Detail;
  analyst?: string;
  analyzedAt?: string;
  decision: string;
  observation: string;
}) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(210px, 0.28fr) minmax(0, 1fr)' }, gap: { xs: 2, md: 3 }, alignItems: 'start' }}>
    <Box sx={{ pr: { md: 2.5 }, borderRight: { md: '1px solid #e6ece7' } }}>
      <RegulatoryMeta patient={patient} analyst={analyst} analyzedAt={analyzedAt} />
    </Box>
    <Stack spacing={2}>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Decisão
        </Typography>
        <Box sx={{ mt: 0.45 }}>
          <RegulatoryDecisionTag decision={decision} />
        </Box>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Justificativa / observação
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.35, whiteSpace: 'pre-wrap', color: observation === '—' ? 'text.secondary' : 'text.primary' }}>
          {observation}
        </Typography>
      </Box>
    </Stack>
  </Box>
);

const RegulatoryDetailsOverview = ({
  patient,
  analyst = '—',
  analyzedAt = '—',
  decision,
  observation,
}: {
  patient: PatientV2Detail;
  analyst?: string;
  analyzedAt?: string;
  decision: string;
  observation: string;
}) => (
  <Stack spacing={2}>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 2 }}>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Status atual
        </Typography>
        <Box sx={{ mt: 0.35 }}>
          <RegulatoryStatusTag status={patient.regulatoryStatus} />
        </Box>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Analista responsável
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600 }}>
          {analyst}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Data da análise
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600 }}>
          {analyzedAt}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Prazo de análise (SLA)
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600 }}>
          —
        </Typography>
      </Box>
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(190px, 0.35fr) minmax(0, 1fr)' }, gap: 2, pt: 1.5, borderTop: '1px solid #e6ece7' }}>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Decisão
        </Typography>
        <Box sx={{ mt: 0.45 }}>
          <RegulatoryDecisionTag decision={decision} />
        </Box>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Observação da análise
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.35, whiteSpace: 'pre-wrap', color: observation === '—' ? 'text.secondary' : 'text.primary' }}>
          {observation}
        </Typography>
      </Box>
    </Box>
  </Stack>
);

interface PatientRegulatoryDecisionProps {
  patient: PatientV2Detail;
  canSave?: boolean;
  approvalMode?: boolean;
  onSave?: (payload: PatientV2RegulatoryDecisionPayload) => Promise<void>;
}

export const PatientRegulatoryDecision = ({ patient, canSave = false, approvalMode = false, onSave }: PatientRegulatoryDecisionProps) => {
  const persisted = patient.regulatoryDecision;
  const [decision, setDecision] = useState<PatientV2RegulatoryDecisionPayload['decision'] | ''>('');
  const [justification, setJustification] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    setDecision('');
    setJustification('');
    setConfirmOpen(false);
    setSaving(false);
    setError('');
    setSubmitAttempted(false);
  }, [patient.id, persisted?.id]);

  if (persisted) {
    const provisional = ['Deferido provisório', 'Indeferimento provisório'].includes(persisted.decision);
    const readOnly = approvalMode ? (
      <RegulatoryReadOnly
        patient={patient}
        analyst={persisted.decidedByName || '—'}
        analyzedAt={formatDateTime(persisted.decidedAt)}
        decision={persisted.decision}
        observation={persisted.justification || 'Nenhuma observação registrada.'}
      />
    ) : (
      <RegulatoryDetailsOverview
        patient={patient}
        analyst={persisted.decidedByName || '—'}
        analyzedAt={formatDateTime(persisted.decidedAt)}
        decision={persisted.decision}
        observation={persisted.justification || 'Nenhuma observação registrada.'}
      />
    );
    return (
      <Stack spacing={1.5}>
        {provisional && (
          <Alert severity="info">
            A decisão <strong>{persisted.decision}</strong> possui uma observação regulatória para acompanhamento.
          </Alert>
        )}
        {readOnly}
      </Stack>
    );
  }

  if (!approvalMode) {
    return (
      <RegulatoryDetailsOverview
        patient={patient}
        decision="Aguardando avaliação"
        observation="A decisão regulatória estará disponível após a aprovação das seções obrigatórias."
      />
    );
  }

  const justificationRequired = Boolean(decision && decision !== 'Deferido');
  const canConfirm = Boolean(decision && canSave);
  const selectedOption = options.find((option) => option.value === decision);
  const submit = async () => {
    if (!decision || !onSave) return;
    setSaving(true);
    setError('');
    try {
      await onSave({ decision, justification: justification.trim() || undefined });
      setConfirmOpen(false);
    } catch (requestError: any) {
      setError(requestError?.message ?? 'Não foi possível registrar a decisão regulatória.');
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };
  const requestConfirmation = () => {
    if (!decision || !canSave) return;
    if (justificationRequired && !justification.trim()) {
      setSubmitAttempted(true);
      return;
    }
    setSubmitAttempted(false);
    setConfirmOpen(true);
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(210px, 0.28fr) minmax(0, 1fr)' }, gap: { xs: 2, md: 3 }, alignItems: 'start' }}>
      <Box sx={{ pr: { md: 2.5 }, borderRight: { md: '1px solid #e6ece7' } }}>
        <RegulatoryMeta patient={patient} />
      </Box>
      <Stack spacing={2}>
        {!canSave && <Alert severity="info">Avaliação das seções concluída. A decisão regulatória está disponível apenas para o perfil avaliador regulatório.</Alert>}
        <FormControl component="fieldset" disabled={!canSave}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.65 }}>
            Decisão
          </Typography>
          <RadioGroup
            value={decision}
            onChange={(event) => {
              setDecision(event.target.value as PatientV2RegulatoryDecisionPayload['decision']);
              setSubmitAttempted(false);
            }}
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: { xs: 0.25, sm: 1 } }}
          >
            {options.map((option) => {
              const style = decisionStyle[option.value];
              const selected = decision === option.value;
              return (
                <Box
                  key={option.value}
                  sx={{
                    minWidth: 0,
                    border: `${selected ? 2 : 1}px solid ${selected ? style.border : '#e0e6e1'}`,
                    borderRadius: 1.5,
                    backgroundColor: selected ? style.background : '#fff',
                    transition: 'border-color 150ms ease, background-color 150ms ease',
                    opacity: canSave ? 1 : 0.62,
                    '&:hover': canSave ? { borderColor: style.border, backgroundColor: style.background } : undefined,
                  }}
                >
                  <FormControlLabel
                    value={option.value}
                    control={<Radio size="small" sx={{ color: style.border, '&.Mui-checked': { color: style.border } }} />}
                    label={
                      <Typography variant="body2" sx={{ lineHeight: 1.25, fontWeight: selected ? 700 : 500 }}>
                        {option.label}
                      </Typography>
                    }
                    sx={{ m: 0, px: 0.75, py: 0.55, width: '100%', minWidth: 0 }}
                  />
                </Box>
              );
            })}
          </RadioGroup>
        </FormControl>
        <TextField
          disabled={!canSave}
          label="Justificativa / observação"
          placeholder={justificationRequired ? 'Informe a justificativa da decisão' : 'Opcional para decisão deferida'}
          multiline
          minRows={3}
          value={justification}
          onChange={(event) => {
            setJustification(event.target.value);
            if (submitAttempted) setSubmitAttempted(false);
          }}
          required={justificationRequired}
          error={Boolean(submitAttempted && justificationRequired && !justification.trim())}
          helperText={
            submitAttempted && justificationRequired && !justification.trim()
              ? 'Informe a justificativa para continuar.'
              : justificationRequired
              ? 'Obrigatória para esta decisão.'
              : 'Opcional para decisão deferida.'
          }
        />
        {error && <Alert severity="error">{error}</Alert>}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<FactCheckOutlined />}
            disabled={!canConfirm || saving}
            onClick={requestConfirmation}
            sx={{ backgroundColor: BrighterGreen, '&:hover': { backgroundColor: DarkGreen }, textTransform: 'none' }}
          >
            Confirmar decisão
          </Button>
        </Box>
      </Stack>
      <Dialog open={confirmOpen} onClose={() => !saving && setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar decisão regulatória?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            A decisão <strong>{selectedOption?.label}</strong> será registrada de forma definitiva nesta solicitação.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setConfirmOpen(false)}>
            Voltar
          </Button>
          <Button variant="contained" disabled={saving} onClick={() => void submit()} sx={{ backgroundColor: BrighterGreen, '&:hover': { backgroundColor: DarkGreen } }}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
